import React, {
  useEffect,
  useState,
} from 'react';
import {
  Outlet,
} from 'react-router-dom';
import isNil from 'lodash/isNil';
import {
  isNilOrZero,
} from '@populateui/utils';
import printJS from 'print-js';
import {
  aiAssistantEventChannel,
  appointmentsEventChannel,
  billingEventChannel,
  faxEventChannel,
  insurancesEventChannel,
  mediaEventChannel,
  patientEventChannel,
  paymentsEventChannel,
  pccEventChannel,
  PCCWalkthroughParams,
  reportEventChannel,
  rtmEventChannel,
  RTMPatientTrendsParams,
  settingsEventChannel,
  toDosEventChannel,
  visitNoteFormEventChannel,
  VisitNoteFormPreviewDialogParams,
} from 'pages/Dashboard/services/eventChannels';
import {
  PCC_LEARN_MORE_URL,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/PCCActivationSettingsPanel';
import PCCWalkthrough from 'views/EMR/components/PCC/Walkthrough';
import Providers from 'views/EMR/Layout/Providers';
import InsuranceDialog, {
  InsuranceDialogParams,
} from 'views/EMR/components/Insurance/Dialog/InsuranceDialog';
import InsuranceBalanceExplanationDialog, {
  InsuranceBalanceExplanationDialogParams,
} from 'pages/Dashboard/pages/Ledger/InsuranceBalanceExplanation/InsuranceBalanceExplanationDialog';
import StatementFormDialog, {
  StatementDialogParams,
} from 'pages/Dashboard/pages/Ledger/StatementFormDialog';
import FinalNoticeFormDialog, {
  FinalNoticeDialogParams,
} from 'pages/Dashboard/pages/Ledger/FinalNoticeFormDialog';
import CreateToDoDialog, {
  ToDoDialogParams,
} from 'pages/Dashboard/pages/Todos/CreateToDoForm';
import {
  useCloseOnRouteChange,
  useNotify,
  useUserSettings,
} from 'core/hooks';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  usePrintPayment,
} from 'pages/Dashboard/pages/Billing/hooks';
import EOBDialog, {
  EOBDialogParams,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog';
import ReconcileDialog from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ReconcileDialog';
import ReportDialog from 'pages/Dashboard/pages/Reports/ReportDialog';
import CreatePatientDialog, {
  PatientDialogParams,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/CreatePatientDialog';
import Topbar from 'views/EMR/Layout/Topbar';
import AppointmentConfirmation, {
  AppointmentConfirmationParams,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/AppointmentConfirmation';
import CreateAppointmentDialog, {
  CreateAppointmentDialogParams,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/CreateAppointmentDialog';
import InsuranceResolutionDialog, {
  InsuranceResolutionDialogParams,
} from 'pages/Dashboard/pages/Billing/Charges/InsuranceResolutionDialog';
import useSignalREffect from 'views/EMR/hooks/signalR';
import Sidebar from 'views/EMR/Layout/Sidebar';
import MedicalBackgroundsDialog, {
  MedicalBackgroundParams,
} from 'views/EMR/components/MedicalBackgroundDialog';
import SendFaxDialog, {
  FaxDialogParams,
} from 'pages/Dashboard/components/SendFaxDialog';
import ReferringProviderContactDialogHandler from 'pages/Dashboard/components/ReferringProviderContactDialogHandler';
import EncountersAllocationDialog, {
  DialogParams as EncountersAllocationDialogParams,
} from 'views/EMR/Billing/EncountersAllocation';
import PaymentDialog, {
  DialogParams as PaymentDialogParams,
} from 'views/EMR/Billing/PaymentDialog';
import AIAssistantDialog from 'views/EMR/components/AIAssistant';
import CreatePurchaseDialog, {
  PurchaseDialogParams,
} from 'views/EMR/Billing/CreatePurchaseDialog';
import PCPDialog, {
  PCPDialogParams,
} from 'views/EMR/components/PCPDialog';
import VitalsTrendsDialog, {
  VitalsTrendsDialogParams,
} from 'views/EMR/components/VitalsTrendsDialog';
import FullScreenSpinner from 'core/components/FullScreenSpinner';
import AdjustPaymentDialog, {
  AdjustPaymentDialogParams,
} from 'views/EMR/Billing/AdjustPayment';
import ProfilePictureDialog, {
  ProfilePictureDialogProps,
} from 'views/EMR/components/ProfilePictureDialog';
import {
  useSnapActions,
  useSnapGeneratedNote,
  useSnapInVisitNote,
  useSnapVisitDetails,
} from 'views/EMR/stores/snap';
import ReviewSOAPDialog, {
  ReviewSOAPDialogParams,
} from 'views/EMR/components/Snap/ReviewSOAPDialog';
import SnapContainer from 'views/EMR/components/Snap';
import EOBScanContainer from 'pages/Dashboard/pages/Billing/Payments/ParseEOBDialog/EOBScanContainer';
import FileUploadDialog, {
  FileUploadDialogProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileUploadDialog';
import FieldDialog, {
  FieldDialogParams,
} from 'views/EMR/Settings/VisitNotes/FieldDialog';
import WebcamScanDialog, {
  WebcamScanDialogProps,
} from 'views/EMR/components/WebcamScanDialog';
import ImageEditorDialog, {
  ImageEditorDialogProps,
} from 'views/EMR/components/ImageEditorDialog';
import ImpersonationBanner from 'core/components/ImpersonationBanner';
import AppVersionUpdateBanner from 'core/components/AppVersionUpdateBanner';
import BulkClaimStatusDialog, {
  BulkClaimStatusDialogProps,
} from 'pages/Dashboard/pages/Billing/Charges/BulkClaim/BulkClaimStatusDialog';
import VisitNoteFormPreviewDialog from 'views/EMR/components/FillableForm/VisitNoteFormPreviewDialog';
import StartFormWorkflowDialog from 'views/EMR/components/FormWorkflow/StartFormWorkflowDialog';
import ClinicianFormStepDialog from 'views/EMR/components/FormWorkflow/ClinicianFormStepDialog';
import {
  StartFormWorkflowParams,
  ClinicianFormStepDialogParams,
} from 'views/EMR/components/FormWorkflow/types';
import {
  usePatientAlertsSignalRInvalidation,
} from 'views/EMR/PatientAlerts/hooks';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import SendConsentFormDialog from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC/SendConsentFormDialog';
import {
  PCCPatientActionContext,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/types';
import PatientTrendsDialog from 'pages/Dashboard/pages/Dashboard/RTM/Trends/PatientTrendsDialog';
import ThresholdSettingsDialog from 'pages/Dashboard/pages/Dashboard/RTM/ThresholdSettings/ThresholdSettingsDialog';
import PCCEnrollDialog from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog';
import {
  datadogLogs,
} from '@datadog/browser-logs';

const PCC_SUPPORT_EMAIL_URL = 'mailto:support@populate.com';

function DashboardOutlet() {
  const [statementDialogParams, setStatementDialogParams] = useState<
    StatementDialogParams | null>(null);
  const [finalNoticeDialogParams, setFinalNoticeDialogParams] = useState<
    FinalNoticeDialogParams | null>(null);
  const [faxParams, setFaxParams] = useState<FaxDialogParams | null>(null);
  const [
    insuranceDialogParams,
    setInsuranceDialogParams,
  ] = useState<InsuranceDialogParams | null>();
  const [
    insuranceBalanceExplanationDialogParams,
    setInsuranceBalanceExplanationDialogParams,
  ] = useState<InsuranceBalanceExplanationDialogParams | null>(null);
  const [
    paymentDialogParams,
    setPaymentsDialogParams,
  ] = useState<PaymentDialogParams | null>(null);
  const [
    toDoDialogParams,
    setToDoDialogParams,
  ] = useState<Omit<ToDoDialogParams, 'onClose'> | null>(null);
  const [
    patientDialogParams,
    setPatientDialogParams,
  ] = useState<PatientDialogParams | null>(null);
  const [
    pccConsentFormParams,
    setPCCConsentFormParams,
  ] = useState<PCCPatientActionContext | null>(null);
  const [
    confirmationDialogParams,
    setConfirmationDialogParams,
  ] = useState<AppointmentConfirmationParams | null>(null);
  const [
    createAppointmentDialogParams,
    setCreateAppointmentDialogParams,
  ] = useState<CreateAppointmentDialogParams | null>(null);
  const [
    insuranceResolveDialogParams,
    setInsuranceResolveDialogParams,
  ] = useState<InsuranceResolutionDialogParams | null>(null);
  const [
    purchaseDialogParams,
    setPurchaseDialogParams,
  ] = useState<PurchaseDialogParams | null>(null);
  const [
    encountersAllocationParams,
    setEncountersAllocationParams,
  ] = useState<EncountersAllocationDialogParams | null>(null);
  const [
    adjustmentDialogParams,
    setAdjustmentDialogParams,
  ] = useState<AdjustPaymentDialogParams | null>(null);

  const [
    profilePictureDialogParams,
    setProfilePictureDialogParams,
  ] = useState<ProfilePictureDialogProps | null>(null);

  const [
    fileUploadDialogParams,
    setFileUploadDialogParams,
  ] = useState<FileUploadDialogProps | null>(null);

  const [
    webcamScanDialogParams,
    setWebcamScanDialogParams,
  ] = useState<WebcamScanDialogProps | null>(null);
  const [
    imageEditorDialogParams,
    setImageEditorDialogParams,
  ] = useState<ImageEditorDialogProps | null>(null);

  const [
    reviewSOAPDialogParams,
    setReviewSOAPDialogParams,
  ] = useState<ReviewSOAPDialogParams | null>(null);

  const [
    fieldDialogParams,
    setFieldDialogParams,
  ] = useState<FieldDialogParams | null>(null);

  const [
    bulkClaimStatusDialogParams,
    setBulkClaimStatusDialogParams,
  ] = useState<BulkClaimStatusDialogProps | null>(null);
  const [
    pccWalkthroughParams,
    setPCCWalkthroughParams,
  ] = useState<PCCWalkthroughParams | null>(null);

  const [pcpDialogParams, setPCPDialogParams] = useState<PCPDialogParams | null>(null);
  const [vitalsTrendsDialogParams, setVitalsTrendsDialogParams] = useState<VitalsTrendsDialogParams | null>(null);

  const [eobDialogParams, setEOBDialogParams] = useState<EOBDialogParams | null>(null);
  const [showReconcile, setShowReconcile] = useState(false);
  const [reportDialogOpened, setReportDialogOpen] = useState<boolean>(false);
  const [
    medicalBackgroundParams,
    setMedicalBackgroundParams,
  ] = useState<MedicalBackgroundParams | null>(null);
  const [aiAssistantDialogOpen, setAIAssistantDialogOpen] = useState<boolean>(false);
  const [
    rtmPatientTrendsParams,
    setRTMPatientTrendsParams,
  ] = useState<RTMPatientTrendsParams | null>(null);
  const [
    rtmThresholdSettingsOpen,
    setRTMThresholdSettingsOpen,
  ] = useState(false);
  const [
    visitNoteFormPreviewParams,
    setVisitNoteFormPreviewParams,
  ] = useState<VisitNoteFormPreviewDialogParams | null>(null);
  const [
    startFormWorkflowParams,
    setStartFormWorkflowParams,
  ] = useState<StartFormWorkflowParams | null>(null);
  const [
    clinicianFormStepParams,
    setClinicianFormStepParams,
  ] = useState<ClinicianFormStepDialogParams | null>(null);

  const userInfo = useUserInfo();
  const notify = useNotify();
  const { mutateAsync: printPayment } = usePrintPayment();

  const onFileStatusChange = (userId: number, state: string, file: 'report' | 'statement') => {
    if (userId === userInfo?.user?.userId) {
      notify({
        variant: state === 'Failed' ? 'error' : 'success',
        message: state === 'Failed' ? `Requested ${file} has failed.` : `Requested ${file} is ready.`,
      });
    }
  };

  useSignalREffect('OnReportCompleted', ({ userId, state }) => {
    onFileStatusChange(userId, state, 'report');
  });

  useSignalREffect('OnBulkPatientStatementCompleted', ({ userId, state }) => {
    onFileStatusChange(userId, state, 'statement');
  });

  useSignalREffect('OnBulkPaperClaimsCompleted', ({ userId, state }) => {
    onFileStatusChange(userId, state, 'report');
  });

  usePatientAlertsSignalRInvalidation();

  useEffect(() => {
    const unsubscribeFromStatementDialog = paymentsEventChannel.on('onStatementDialog', (params) => {
      setStatementDialogParams(params);
    });
    const unsubscribeFromFinalNoticeDialog = paymentsEventChannel.on('onFinalNoticeDialog', (params) => {
      setFinalNoticeDialogParams(params);
    });
    const unsubscribeFromInsurance = insurancesEventChannel.on('openInsuranceDialog', (params) => {
      setInsuranceDialogParams(params);
    });
    const unsubscribeFromInsuranceBalanceExplanation = billingEventChannel.on(
      'onInsuranceBalanceExplanationDialogOpen',
      (params) => {
        setInsuranceBalanceExplanationDialogParams(params);
      },
    );

    const unsubscribeFromPayment = paymentsEventChannel.on('onPaymentDialogOpen', (params) => {
      setPaymentsDialogParams(params);
    });

    const unsubscribeFromEOB = paymentsEventChannel.on('onEOBDialogOpen', (params) => {
      setEOBDialogParams(params);
      const {
        eobExtractionJobId,
        parsedEOBErrors,
      } = params ?? {};
      const {
        claimsPresentInEobButMissingInSystem,
        isPayerMissingInSystem = false,
      } = parsedEOBErrors ?? {};

      const isClaimsMissing = (claimsPresentInEobButMissingInSystem?.length ?? 0) > 0;
      const needsReconcile = !isNil(eobExtractionJobId)
        && (isClaimsMissing || isPayerMissingInSystem);

      setShowReconcile(needsReconcile);
    });

    const unsubscribeFromPatientDialog = patientEventChannel.on('onCreatePatientDialog', (params = {}) => {
      setPatientDialogParams(params);
    });
    const unsubscribeFromPCCConsentForm = patientEventChannel.on('onShowPCCConsentForm', (params) => {
      setPCCConsentFormParams(params);
    });

    const unsubscribeFromEditMedicalBackground = patientEventChannel.on('onEditMedicalBackground', (params) => {
      setMedicalBackgroundParams(params);
    });

    const unsubscribeFromViewProfilePicture = patientEventChannel.on('onViewProfilePicture', (params) => {
      setProfilePictureDialogParams(params);
    });

    const unsubscribeFromToDo = toDosEventChannel.on('onCreateToDo', (params) => {
      setToDoDialogParams(params);
    });

    const unsubscribeFromReport = reportEventChannel.on('onReportCreate', () => {
      setReportDialogOpen(true);
    });

    const unsubscribeFromCreate = appointmentsEventChannel.on('onEventCreate', (params) => {
      setCreateAppointmentDialogParams(params);
    });
    const unsubscribeFromConfirmAppointment = appointmentsEventChannel.on('onAppointmentConfirmation', (params) => {
      setConfirmationDialogParams(params);
    });
    const unsubscribeFromInsuranceResolve = billingEventChannel.on('onInsuranceResolve', (params) => {
      setInsuranceResolveDialogParams(params);
    });
    const unsubscribeFromPurchaseCreate = billingEventChannel.on('onPurchaseCreate', (params) => {
      setPurchaseDialogParams(params);
    });

    const unsubscribeFromFaxDialog = faxEventChannel.on('onOpenSendFax', (params = {}) => {
      setFaxParams(params);
    });

    const unsubscribeFromVisitNoteFormPreview = visitNoteFormEventChannel.on(
      'onOpenVisitNoteFormPreview',
      (params) => { setVisitNoteFormPreviewParams(params); },
    );

    const unsubscribeFromStartFormWorkflow = visitNoteFormEventChannel.on(
      'onOpenStartFormWorkflow',
      (params) => { setStartFormWorkflowParams(params); },
    );

    const unsubscribeFromClinicianFormStep = visitNoteFormEventChannel.on(
      'onOpenClinicianFormStep',
      (params) => { setClinicianFormStepParams(params); },
    );

    const unsubscribeEncountersAllocation = billingEventChannel.on('onEncountersAllocation', (params) => {
      setEncountersAllocationParams(params);
    });

    const unsubscribeFromAdjustPayment = paymentsEventChannel.on('onAdjustPayment', (params) => {
      setAdjustmentDialogParams(params);
    });

    const unsubscribeFromAIAssistant = aiAssistantEventChannel.on('onShowAIAssistantDialog', () => {
      setAIAssistantDialogOpen(true);
    });

    const unsubscribeFromRTMPatientTrends = rtmEventChannel.on(
      'onShowRTMPatientTrends',
      (params) => {
        setRTMPatientTrendsParams(params);
      },
    );

    const unsubscribeFromRTMThresholdSettings = rtmEventChannel.on(
      'onShowRTMThresholdSettings',
      () => {
        setRTMThresholdSettingsOpen(true);
      },
    );

    const unsubscribeFromEditPCP = patientEventChannel.on('onEditPCP', (params) => {
      setPCPDialogParams(params);
    });
    const unsubscribeFromVitalsTrends = patientEventChannel.on('onOpenVitalsTrends', (params) => {
      setVitalsTrendsDialogParams(params);
    });

    const unsubscribeFromUploadMediaToCharts = mediaEventChannel.on('onUploadMediaToCharts', (params) => {
      setFileUploadDialogParams(params);
    });

    const unsubscribeFromStartWebcamScan = mediaEventChannel.on('onStartWebcamScan', (params) => {
      setWebcamScanDialogParams(params);
    });

    const unsubscribeFromOpenImageEditor = mediaEventChannel.on('onOpenImageEditorDialog', (params) => {
      setImageEditorDialogParams(params);
    });

    const unsubscribeFromTemplateFieldDialog = settingsEventChannel.on('onTemplateFieldDialog', (payload) => {
      setFieldDialogParams(payload ?? null);
    });

    const unsubscribeFromBulkClaimStatusDialog = billingEventChannel.on('onBulkClaimStatusDialogOpen', (params) => {
      setBulkClaimStatusDialogParams(params);
    });

    const unsubscribeFromPCCWalkthrough = pccEventChannel.on(
      'onShowPCCWalkthrough',
      (params = {}) => {
        setPCCWalkthroughParams(params);
      },
    );

    return () => {
      unsubscribeFromInsurance();
      unsubscribeFromInsuranceBalanceExplanation();
      unsubscribeFromPayment();
      unsubscribeFromPatientDialog();
      unsubscribeFromPCCConsentForm();
      unsubscribeFromEOB();
      unsubscribeFromToDo();
      unsubscribeFromReport();
      unsubscribeFromCreate();
      unsubscribeFromPurchaseCreate();
      unsubscribeFromConfirmAppointment();
      unsubscribeFromInsuranceResolve();
      unsubscribeFromEditMedicalBackground();
      unsubscribeFromFaxDialog();
      unsubscribeFromVisitNoteFormPreview();
      unsubscribeFromStartFormWorkflow();
      unsubscribeFromClinicianFormStep();
      unsubscribeEncountersAllocation();
      unsubscribeFromAdjustPayment();
      unsubscribeFromStatementDialog();
      unsubscribeFromFinalNoticeDialog();
      unsubscribeFromAIAssistant();
      unsubscribeFromRTMPatientTrends();
      unsubscribeFromRTMThresholdSettings();
      unsubscribeFromEditPCP();
      unsubscribeFromVitalsTrends();
      unsubscribeFromViewProfilePicture();
      unsubscribeFromUploadMediaToCharts();
      unsubscribeFromTemplateFieldDialog();
      unsubscribeFromStartWebcamScan();
      unsubscribeFromOpenImageEditor();
      unsubscribeFromBulkClaimStatusDialog();
      unsubscribeFromPCCWalkthrough();
    };
  }, []);

  useEffect(() => {
    const unsubscribeFromPaymentPrint = paymentsEventChannel.on('onPaymentPrint', async (payment, onComplete) => {
      try {
        const response = await printPayment({
          patientPaymentTransactionId: payment?.patientPaymentTransactionId ?? 0,
          type: payment?.type,
        });
        if (!isNil(response)) {
          printJS({
            printable: response?.url?.value,
            type: 'pdf',
            showModal: true,
          });
        }
        onComplete?.(true);
      } catch (error) {
        onComplete?.(false);
      }
    });

    return () => {
      unsubscribeFromPaymentPrint();
    };
  }, [userInfo]);

  const { loadingUserSettings } = useUserSettings();
  const inVisitNote = useSnapInVisitNote();
  const visitDetails = useSnapVisitDetails();
  const generatedNote = useSnapGeneratedNote();
  const { closeSnap } = useSnapActions();

  useEffect(() => {
    if (!inVisitNote && !isNil(generatedNote) && !isNil(visitDetails)) {
      datadogLogs.logger.info(`[Snap] Opening review SOAP dialog`);
      setReviewSOAPDialogParams({
        notes: generatedNote,
        visitDetails,
      });
      closeSnap();
    }
  }, [inVisitNote, generatedNote]);

  useCloseOnRouteChange(() => {
    setRTMPatientTrendsParams(null);
    setRTMThresholdSettingsOpen(false);
    setPCCWalkthroughParams(null);
  });

  return (
    <>
      <ImpersonationBanner />
      <AppVersionUpdateBanner />
      <div className="outlet-container h-full w-full flex bg-primaryLight text-white">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col text-primary">
          <div className="flex gap-4 px-3 py-3 nw-topbar">
            <Topbar />
          </div>
          <div className="flex-1 mx-3 mb-3 overflow-x-hidden nw-page-content">
            <div className="h-full w-full">
              <div className="relative flex gap-2 h-full max-h-[calc(100vh-1.5rem)] overflow-hidden">
                <div className="flex flex-col w-full h-full bg-white rounded-xl overflow-hidden">
                  {loadingUserSettings ? (
                    <FullScreenSpinner />
                  ) : (
                    <Outlet />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isNil(insuranceDialogParams) && (
        <InsuranceDialog
          open
          onClose={() => { setInsuranceDialogParams(null); }}
          {...(insuranceDialogParams ?? {})}
        />
      )}
      {!isNil(insuranceBalanceExplanationDialogParams) && (
        <InsuranceBalanceExplanationDialog
          {...insuranceBalanceExplanationDialogParams}
          onClose={() => { setInsuranceBalanceExplanationDialogParams(null); }}
        />
      )}
      {!isNil(paymentDialogParams) && (
        <PaymentDialog
          onClose={() => { setPaymentsDialogParams(null); }}
          {...(paymentDialogParams ?? {})}
        />
      )}
      {!isNil(toDoDialogParams) && (
        <CreateToDoDialog
          onClose={() => { setToDoDialogParams(null); }}
          {...(toDoDialogParams ?? {})}
        />
      )}
      {!isNil(eobDialogParams) && showReconcile
        && [
          eobDialogParams.eobExtractionJobId,
          eobDialogParams.parsedEOBData,
          eobDialogParams.parsedEOBErrors,
        ].every((param) => !isNil(param)) && (
          <ReconcileDialog
            eobExtractionJobId={eobDialogParams.eobExtractionJobId ?? 0}
            eobNumber={eobDialogParams.eobNumber}
            parsedEOBData={eobDialogParams.parsedEOBData ?? {}}
            parsedEOBErrors={eobDialogParams.parsedEOBErrors ?? {}}
            parsedEOBFile={eobDialogParams.parsedEOBFile}
            onContinue={(updatedData, updatedErrors) => {
              setEOBDialogParams((prev) => ({
                ...prev,
                parsedEOBData: updatedData,
                parsedEOBErrors: updatedErrors,
              }));
              setShowReconcile(false);
            }}
            onClose={() => {
              setEOBDialogParams(null);
              setShowReconcile(false);
            }}
          />
        )}
      {!isNil(eobDialogParams) && !showReconcile && (
        <EOBDialog
          onClose={() => { setEOBDialogParams(null); }}
          {...eobDialogParams}
        />
      )}
      {reportDialogOpened && <ReportDialog onClose={() => { setReportDialogOpen(false); }} />}
      {!isNil(patientDialogParams) && (
        <CreatePatientDialog
          onClose={() => { setPatientDialogParams(null); }}
          {...patientDialogParams}
        />
      )}
      {!isNil(pccConsentFormParams) && (
        <SendConsentFormDialog
          patientId={pccConsentFormParams.patientId}
          patientEmail={pccConsentFormParams.patientEmail}
          patientPhone={pccConsentFormParams.patientPhone}
          onClose={() => { setPCCConsentFormParams(null); }}
        />
      )}
      {!isNil(medicalBackgroundParams) && (
        <MedicalBackgroundsDialog
          onClose={() => { setMedicalBackgroundParams(null); }}
          {...medicalBackgroundParams}
        />
      )}
      {!isNil(createAppointmentDialogParams) && (
        <CreateAppointmentDialog
          {...createAppointmentDialogParams}
          open
          onClose={() => { setCreateAppointmentDialogParams(null); }}
        />
      )}
      {!isNil(insuranceResolveDialogParams) && (
        <InsuranceResolutionDialog
          {...insuranceResolveDialogParams}
          onClose={() => { setInsuranceResolveDialogParams(null); }}
        />
      )}
      {!isNil(confirmationDialogParams) && (
        <AppointmentConfirmation
          {...confirmationDialogParams}
          onClose={() => { setConfirmationDialogParams(null); }}
        />
      )}
      {!isNil(purchaseDialogParams) && (
        <CreatePurchaseDialog
          {...purchaseDialogParams}
          onClose={() => { setPurchaseDialogParams(null); }}
        />
      )}
      {!isNil(faxParams) && (
        <SendFaxDialog
          {...(faxParams ?? {})}
          open
          onClose={() => { setFaxParams(null); }}
        />
      )}
      <ReferringProviderContactDialogHandler />
      {!isNil(visitNoteFormPreviewParams) && (
        <VisitNoteFormPreviewDialog
          {...visitNoteFormPreviewParams}
          onClose={() => { setVisitNoteFormPreviewParams(null); }}
        />
      )}
      {!isNil(startFormWorkflowParams) && (
        <StartFormWorkflowDialog
          {...startFormWorkflowParams}
          onClose={() => { setStartFormWorkflowParams(null); }}
        />
      )}
      {!isNil(clinicianFormStepParams) && (
        <ClinicianFormStepDialog
          {...clinicianFormStepParams}
          onClose={() => { setClinicianFormStepParams(null); }}
        />
      )}
      {!isNil(encountersAllocationParams) && !isNilOrZero(encountersAllocationParams.patient?.patientId) && (
        <EncountersAllocationDialog
          {...(encountersAllocationParams ?? {})}
          onClose={() => { setEncountersAllocationParams(null); }}
        />
      )}
      {!isNil(adjustmentDialogParams) && (
        <AdjustPaymentDialog
          {...(adjustmentDialogParams ?? {})}
          onClose={() => { setAdjustmentDialogParams(null); }}
        />
      )}
      {!isNil(statementDialogParams) && (
        <StatementFormDialog
          {...statementDialogParams}
          onClose={() => { setStatementDialogParams(null); }}
        />
      )}
      {!isNil(finalNoticeDialogParams) && (
        <FinalNoticeFormDialog
          {...finalNoticeDialogParams}
          onClose={() => { setFinalNoticeDialogParams(null); }}
        />
      )}
      {aiAssistantDialogOpen && (
        <AIAssistantDialog
          onClose={() => { setAIAssistantDialogOpen(false); }}
        />
      )}
      {!isNil(rtmPatientTrendsParams) && (
        <PatientTrendsDialog
          {...rtmPatientTrendsParams}
          onClose={() => { setRTMPatientTrendsParams(null); }}
        />
      )}
      {rtmThresholdSettingsOpen && (
        <ThresholdSettingsDialog
          onClose={() => { setRTMThresholdSettingsOpen(false); }}
        />
      )}
      {!isNil(pcpDialogParams) && (
        <PCPDialog
          onClose={() => { setPCPDialogParams(null); }}
          {...pcpDialogParams}
        />
      )}
      {!isNil(vitalsTrendsDialogParams) && (
        <VitalsTrendsDialog
          onClose={() => { setVitalsTrendsDialogParams(null); }}
          {...vitalsTrendsDialogParams}
        />
      )}
      {!isNil(profilePictureDialogParams) && (
        <ProfilePictureDialog
          {...profilePictureDialogParams}
          onClose={() => { setProfilePictureDialogParams(null); }}
        />
      )}
      {!isNil(reviewSOAPDialogParams) && (
        <ReviewSOAPDialog
          {...reviewSOAPDialogParams}
          onClose={() => { setReviewSOAPDialogParams(null); }}
        />
      )}
      {!isNil(fileUploadDialogParams) && (
        <FileUploadDialog
          {...fileUploadDialogParams}
          onClose={() => { setFileUploadDialogParams(null); }}
        />
      )}
      {!isNil(webcamScanDialogParams) && (
        <WebcamScanDialog
          {...webcamScanDialogParams}
          onClose={() => { setWebcamScanDialogParams(null); }}
        />
      )}
      {!isNil(imageEditorDialogParams) && (
        <ImageEditorDialog
          {...imageEditorDialogParams}
          onClose={() => { setImageEditorDialogParams(null); }}
        />
      )}
      {!isNil(fieldDialogParams) && (
        <FieldDialog
          {...fieldDialogParams}
          onClose={() => { setFieldDialogParams(null); }}
        />
      )}
      {!isNil(bulkClaimStatusDialogParams) && (
        <BulkClaimStatusDialog
          {...bulkClaimStatusDialogParams}
          onClose={() => { setBulkClaimStatusDialogParams(null); }}
        />
      )}
      {!isNil(pccWalkthroughParams) && (
        <PCCWalkthrough
          open
          onClose={() => { setPCCWalkthroughParams(null); }}
          organizationName={
            userInfo?.organization?.name
            ?? userInfo?.organization?.displayName
            ?? ''
          }
          guideUrl={PCC_LEARN_MORE_URL}
          supportUrl={PCC_SUPPORT_EMAIL_URL}
          initialPageId={pccWalkthroughParams.initialPageId ?? 'overview'}
          onReturnToVisitNote={pccWalkthroughParams.showReturnToVisitNote
            ? () => { setPCCWalkthroughParams(null); }
            : undefined}
        />
      )}
      <PCCEnrollDialog />
      <SnapContainer />
      <EOBScanContainer />
    </>
  );
}

export default function Layout() {
  const {
    isRestricted,
    userInfo,
  } = useEMRAccess();
  const canUseRestrictedChrome = !isNil(userInfo) && !isRestricted;

  return (
    <Providers
      canUseMedia={canUseRestrictedChrome}
      canUseRealTime={canUseRestrictedChrome}
    >
      <DashboardOutlet />
    </Providers>
  );
}
